// Third party
import React from 'react';
import cropperjs from 'cropperjs';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

// Components
import { PrimaryButton, SecondaryLink } from '../../buttons';
import { LoadingSpinner } from '../../common';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { cropUserImg, getMe, uploadImg } from '../../../../redux/modules/auth';
import { waitJob } from '../../../../redux/modules/jobs';

// Utils
import notification from '../../../../utils/notification';
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import AdvisorContext from '@/types/advisor-context';
import Nullable from '@/types/nullable';

type DispatchProps = {
  waitJob: (id: string) => Promise<void>;
  getMe: Function;
  uploadImg: (file: File) => Promise<string>;
};

type StateProps = {
  context?: AdvisorContext | null;
};

type Props = StateProps &
  DispatchProps & {
    onUpload: (imgUrl: string, fileType: string, dimensions: cropperjs.Data) => Promise<string>;
    errorKey: string;
    onSave?: Function;
    aspectRatio?: number;
    targetImg?: Nullable<string>;
    labelPrefix: string;
  };

type State = {
  imgUrl?: string;
  fileType?: string;
  saving?: boolean;
};

class ImageUpload extends React.Component<Props, State> {
  readonly state: State = {};

  componentDidMount(): void {
    const { targetImg } = this.props;
    if (targetImg) {
      this.setState({ imgUrl: targetImg });
      fetch(targetImg).then(res => {
        res.blob().then(blob => this.setState({ fileType: blob.type }));
      });
    }
  }

  onDropAccepted = (files: File[]) => {
    const file = files[0];

    this.props.uploadImg(file).then(imgUrl => {
      this.setState({ imgUrl, fileType: file.type });
    });
  };

  resetImage = () => {
    this.setState({ imgUrl: undefined });
  };

  handleSaveImage = () => {
    this.disableCropper();

    const { imgUrl, fileType } = this.state;

    if (!imgUrl || !fileType) {
      return;
    }
    this.props.onUpload(imgUrl, fileType, this.getCropperData()!).then((jobId: string) => {
      this.setState({ saving: true });
      this.props.waitJob(jobId).then(this.onCropImgFinished).catch(this.onCropImgError);
    });
  };

  onCropImgFinished = () => {
    const { context, labelPrefix } = this.props;
    const contextId = context != null ? context.contextId : undefined;
    this.enableCropper();
    this.props.getMe(contextId).then(() => {
      notification.success(formatMessage(`${labelPrefix}.SaveSuccess`));
      this.setState({ saving: false });

      if (this.props.onSave) {
        this.props.onSave();
      }
    });
  };

  onCropImgError = () => {
    this.enableCropper();
    this.setState({ saving: false });
    notification.errorFromKey(this.props.errorKey);
  };

  enableCropper = () => {
    const cropper = this.refs.cropper as Cropper;
    if (!cropper) {
      return;
    }
    cropper.enable();
  };

  disableCropper = () => {
    const cropper = this.refs.cropper as Cropper;
    if (!cropper) {
      return;
    }
    cropper.disable();
  };

  getCropperData = () => {
    const cropper = this.refs.cropper as Cropper;
    if (!cropper) {
      return;
    }
    return cropper.getData(true);
  };

  render() {
    const { aspectRatio, labelPrefix } = this.props;
    const { imgUrl } = this.state;

    return (
      <div className="image-upload d-flex">
        {imgUrl && (
          <div className="d-flex flex-column">
            <Cropper className="img-cropper" ref="cropper" src={imgUrl} aspectRatio={aspectRatio} guides={false} preview=".cropper-preview" dragMode="move" />

            <PrimaryButton className="save-image" onClick={this.handleSaveImage} disabled={this.state.saving}>
              {this.state.saving ? <LoadingSpinner /> : <FormattedMessage id={`${labelPrefix}.Save`} />}
            </PrimaryButton>

            {!this.state.saving && (
              <SecondaryLink onClick={this.resetImage}>
                <FormattedMessage id={`${labelPrefix}.UploadNewPicture`} />
              </SecondaryLink>
            )}
          </div>
        )}

        {!imgUrl && (
          <Dropzone
            onDropAccepted={this.onDropAccepted}
            accept={{
              'image/png': ['.png'],
              'image/jpg': ['.jpg'],
              'image/jpeg': ['.jpeg'],
              'image/bmp': ['.bmp'],
            }}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className="image-upload-drop">
                <input {...getInputProps()} />

                <div className="image-drop-content d-flex">
                  <div className="upload-icon d-flex flex-column">
                    <i className="icon-upload" />
                  </div>

                  <div className="image-drop-text flex-grow-1">
                    <FormattedMessage id={`${labelPrefix}.Drag`} />
                    <div className="text-separator">
                      <FormattedMessage id={`${labelPrefix}.Or`} />
                    </div>
                    <FormattedMessage id={`${labelPrefix}.Select`} />
                  </div>
                </div>
              </div>
            )}
          </Dropzone>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  context: state.auth.context,
});

export default connect(mapStateToProps, {
  uploadImg,
  cropUserImg,
  waitJob,
  getMe,
})(ImageUpload);
